import React from 'react'
import CopyIcon from '../icons/copy.svg'
import { scanLinks } from '../utils/constants.ts'
import TokenLogoImage from './TokenLogoImage.jsx'
import { calculateBondingProgress } from '../utils/bondingCurve.ts'

export default function TokenDetails({
  contractAddress,
  tokenName,
  tokenSymbol,
  description,
  website,
  twitter,
  telegram,
  tokenAddress,
  isTooltipDisplayed,
  copyAddress,
  maxWalletLimit,
  maxBuyAmount,
  volume,
  lpCreated,
  tokenPrice,
  tokenSupplyUSD,
  tokenSupplyLiquidity,
  chainId,
  versionType
}) {
  const pricePerToken = tokenSupplyUSD / tokenSupplyLiquidity / 2
  const marketCap = pricePerToken * 1000000000
  const bondingProgress = calculateBondingProgress({
    chainId,
    versionType,
    tokenPriceRaw: tokenPrice,
    lpCreated
  })
  const tvl = tokenSupplyUSD
  const logoAddress = contractAddress || tokenAddress

  return (
    <div className="w-full p-6 bg-[#171a21] text-white rounded-lg">
      {/* Header row: image + name/ticker (row), description below */}
      <div className="flex flex-col gap-2 overflow-hidden">
        <div className="flex items-center gap-4">
          <TokenLogoImage
            address={logoAddress}
            chainId={chainId}
            version={versionType}
            alt="Token Cover"
            className="w-16 h-16 object-cover rounded-lg"
            loading="lazy"
            decoding="async"
          />
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <h2
              className="text-[22px] sm:text-[26px] lg:text-[32px] leading-none font-extrabold text-[#f8ffe8] truncate max-w-full"
              title={`${tokenName} (${tokenSymbol})`}
            >
              {tokenName}
            </h2>
            <span
              className="px-2 py-0.5 rounded-[8px] border border-[#2a2d36] bg-[#2a2d36]/40 text-[#f8ffe8]/90 uppercase tracking-wide text-[11px] sm:text-[12px]"
              aria-label="token symbol"
            >
              {tokenSymbol}
            </span>
          </div>
        </div>
        <p className="text-sm text-[#f8ffe8]/80">
          {description?.length > 120
            ? description.slice(0, 120) + '...'
            : description}
        </p>
      </div>

      {/* Bonding curve progress */}
      <div className="mt-4">
        <p className="text-xs tracking-wide text-[#f8ffe8]/60 mb-1">
          bonding curve progress: {bondingProgress.toFixed(2)}%
        </p>
        <div className="relative w-full h-3 bg-[#333] rounded-full overflow-hidden">
          <div
            className="absolute top-0 left-0 h-full bg-[#f500ff]"
            style={{ width: `${bondingProgress}%` }}
          />
        </div>
      </div>

      {/* Social links */}
      <div className="flex flex-wrap gap-2 mt-4">
        {website && (
          <a
            href={website}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1 rounded-lg border border-[#2a2d36] bg-[#2a2d36]/40 hover:bg-[#2a2d36] hover:border-[#a135bb] transition-colors hover:translate-y-[-1px] text-sm"
          >
            website
          </a>
        )}
        {twitter && (
          <a
            href={twitter}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1 rounded-lg border border-[#2a2d36] bg-[#2a2d36]/40 hover:bg-[#2a2d36] hover:border-[#a135bb] transition-colors hover:translate-y-[-1px] text-sm"
          >
            twitter
          </a>
        )}
        {telegram && (
          <a
            href={telegram}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1 rounded-lg border border-[#2a2d36] bg-[#2a2d36]/40 hover:bg-[#2a2d36] hover:border-[#a135bb] transition-colors hover:translate-y-[-1px] text-sm"
          >
            telegram
          </a>
        )}
      </div>

      {/* Token Info Metrics (styled like Token Address section) */}
      {!lpCreated && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="flex flex-col bg-[#1d2028] p-3 rounded-lg">
            <span className="text-xs text-gray-400 mb-1">max wallet:</span>
            <span className="text-[12px] text-[#f8ffe8]">{maxWalletLimit}</span>
          </div>
          <div className="flex flex-col bg-[#1d2028] p-3 rounded-lg">
            <span className="text-xs text-gray-400 mb-1">max bag:</span>
            <span className="text-[12px] text-[#f8ffe8]">{maxBuyAmount}</span>
          </div>
          <div className="flex flex-col bg-[#1d2028] p-3 rounded-lg">
            <span className="text-xs text-gray-400 mb-1">market cap:</span>
            <span className="text-[12px] text-[#f8ffe8]">
              $
              {marketCap.toLocaleString(undefined, {
                maximumFractionDigits: 0
              })}
            </span>
          </div>
          <div className="flex flex-col bg-[#1d2028] p-3 rounded-lg">
            <span className="text-xs text-gray-400 mb-1">tvl:</span>
            <span className="text-[12px] text-[#f8ffe8]">
              ${tvl.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          </div>
          <div className="flex flex-col bg-[#1d2028] p-3 rounded-lg">
            <span className="text-xs text-gray-400 mb-1">volume:</span>
            <span className="text-[12px] text-[#f8ffe8]">
              $
              {(volume / 10 ** 18).toLocaleString(undefined, {
                maximumFractionDigits: 0
              })}
            </span>
          </div>
          <div className="flex flex-col bg-[#1d2028] p-3 rounded-lg">
            <span className="text-xs text-gray-400 mb-1">token price:</span>
            <span className="text-[12px] text-[#f8ffe8]">
              ${pricePerToken.toFixed(8)}
            </span>
          </div>
        </div>
      )}

      {/* Copyable Contract Address */}
      <div className="mt-3 rounded-lg border border-[#2a2d36] bg-[#171a21] flex items-center gap-2 w-full px-3 py-2">
        <span className="text-xs text-gray-400 mr-2">token address:</span>
        <a
          href={`${scanLinks[chainId]}address/${tokenAddress}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 text-[12px] text-[#f8ffe8] overflow-hidden hover:underline"
        >
          {isTooltipDisplayed
            ? 'copied'
            : `${tokenAddress.slice(0, 8)}...${tokenAddress.slice(-8)}`}
        </a>
        <button
          type="button"
          className="inline-flex justify-center items-center rounded-[8px] border border-[#2a2d36] bg-[#2a2d36]/40 hover:bg-[#2a2d36] hover:border-[#a135bb] transition-colors hover:translate-y-[-1px] w-9 h-8 min-w-[36px]"
          onClick={copyAddress(tokenAddress)}
          aria-label="Copy token address to clipboard"
        >
          {isTooltipDisplayed ? (
            // inline checkmark to avoid image loading jitter
            <svg
              viewBox="0 0 20 20"
              width="16"
              height="16"
              aria-hidden="true"
              className="w-4 h-4"
            >
              <path
                fill="currentColor"
                d="M7.7 13.3l-3-3a1 1 0 111.4-1.4l2.3 2.3 5.5-5.5a1 1 0 111.4 1.4l-6.2 6.2a1 1 0 01-1.4 0z"
              />
            </svg>
          ) : (
            <img
              src={CopyIcon}
              alt=""
              aria-hidden="true"
              decoding="async"
              loading="lazy"
              fetchpriority="low"
              width="16"
              height="16"
              className="w-4 h-4"
            />
          )}
        </button>
      </div>
    </div>
  )
}
